"use client"
import { useEffect } from 'react';

const TipsPage = ({ onBack }) => {
  // Define a mapping of tip numbers to Tailwind color classes
  const colorMap = {
    1: { bg: 'bg-emerald-100', text: 'text-emerald-700' },
    2: { bg: 'bg-blue-100', text: 'text-blue-700' },
    3: { bg: 'bg-amber-100', text: 'text-amber-700' },
    4: { bg: 'bg-red-100', text: 'text-red-700' },
    5: { bg: 'bg-purple-100', text: 'text-purple-700' },
    6: { bg: 'bg-teal-100', text: 'text-teal-700' },
  };

  // Scroll to top when the component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []); // Empty dependency array ensures this runs only once on mount

  return (
    <div className="min-h-screen py-44 bg-gradient-to-b from-emerald-50 via-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-12">
          <button
            onClick={onBack}
            className="flex items-center text-emerald-700 hover:text-emerald-800 mb-4 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Products
          </button>
          <h1 className="text-4xl md:text-5xl font-bold text-emerald-800 mb-3">Mediwhistle Patient Tips & Tricks</h1>
          <p className="text-lg text-charcoal-600">
            Designed to help you get the best medical outcomes from your prescribed cannabis treatment.
          </p>
        </div>
        {/* Content */}
        <div className="space-y-8">
          {[
            {
              number: 1,
              title: "Using Your Mediwhistle Device",
              items: [
                "Mediwhistle activates automatically when you inhale — no buttons required",
                "Take slow, steady breaths rather than sharp pulls. This ensures smoother vapour delivery and more consistent dosing",
                "The oil chamber lights up during use, allowing you to see remaining medication"
              ]
            },
            {
              number: 2,
              title: "Charging & Power",
              items: [
                "Mediwhistle uses a USB-C charger (common phone charger). Do not use high-voltage “fast chargers” — a standard 5V charger is safest",
                "While charging: light is on",
                "Fully charged: light turns off",
                "Keep your device charged regularly to ensure the best outcomes"
              ]
            },
            {
              number: 3,
              title: "Preventing Blockages",
              items: [
                "Keep the device at room temperature (extreme cold thickens oil, heat can cause leaks)",
                {
                  text: "If the airflow feels blocked, try:",
                  subItems: [
                    "Warming gently in your hand for a few seconds",
                    "Blowing once into the bottom airhole, then inhaling again"
                  ]
                },
                "Store upright to reduce the risk of leaks or clogs"
              ]
            },
            {
              number: 4,
              title: "Safe Storage",
              items: [
                "Store at normal room temperature, away from direct sunlight or freezing conditions",
                "Keep upright and in a clean, dry place",
                "Do not leave the device in hot environments such as cars",
                "Store safely out of reach of children and pets"
              ]
            },
            {
              number: 5,
              title: "Care & Maintenance",
              items: [
                "Clean the mouthpiece occasionally with a tissue",
                "If residue builds near the charging port, clean carefully with a cotton swab lightly dampened with isopropyl alcohol — ensure completely dry before charging",
                "Avoid dropping the device as this may affect safety and performance"
              ]
            },
            {
              number: 6,
              title: "Medical Use & Best Practice",
              items: [
                "Wait a few minutes between inhalations to allow effects to be felt before taking additional doses",
                "Avoid “chain use” — repeated inhalations may reduce flavour consistency and make dosing less predictable",
                "When travelling by plane, store Mediwhistle upside down to prevent leaks due to pressure changes",
                "Consult your prescribing doctor if you experience unexpected effects or device issues"
              ]
            }
          ].map((tip) => (
            <div
              key={tip.number}
              className={`bg-white rounded-2xl border border-stone-200 p-6 hover:shadow-xl transition-all duration-300`}
            >
              <div className="flex items-start space-x-4">
                <div className={`${colorMap[tip.number].bg} w-14 h-14 rounded-lg flex items-center justify-center flex-shrink-0`}>
                  <span className={`${colorMap[tip.number].text} font-bold text-lg`}>{tip.number}</span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-charcoal-800 mb-3">{tip.title}</h3>
                  <ul className="text-charcoal-700 space-y-2">
                    {tip.items.map((item, idx) => (
                      <li key={idx}>
                        {typeof item === 'string' ? (
                          `• ${item}`
                        ) : (
                          <>
                            • {item.text}
                            <ul className="ml-6 space-y-1">
                              {item.subItems.map((subItem, subIdx) => (
                                <li key={subIdx}>◦ {subItem}</li>
                              ))}
                            </ul>
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default TipsPage